/** O Documento: uma página sobre o modelo, impressa em PDF.
 *
 *  Porquê. A bSPT vive de relatórios, e hoje o que a plataforma sabe
 *  sobre um modelo (quantos elementos, de que classes, que pisos, que
 *  tópicos BCF abertos) tem de ser copiado à mão para um Word. Na
 *  revisão seguinte do IFC, copia-se tudo outra vez.
 *
 *  A ideia que torna isto útil: **guardam-se as ligações, não os
 *  valores**. Um parágrafo não diz "o modelo tem 137 elementos", diz
 *  "o modelo tem {elementos} elementos". O mesmo documento aberto sobre
 *  a revisão seguinte lê a revisão seguinte. Um relatório que guardasse
 *  os números seria uma fotografia, e uma fotografia desactualiza-se
 *  sem avisar.
 *
 *  Este ficheiro tem o modelo de dados e a resolução das ligações, sem
 *  React e sem PDF, para se poder testar.
 */

export type TipoBloco = 'titulo' | 'subtitulo' | 'paragrafo' | 'tabela-classes' | 'topicos-bcf' | 'quebra'

export interface BlocoDocumento {
  id: string
  tipo: TipoBloco
  /** Texto com ligações entre chavetas. Ignorado nos blocos que não
   *  têm texto próprio. */
  texto: string
}

export interface Documento {
  id: string
  nome: string
  blocos: BlocoDocumento[]
}

/** Os dados de onde as ligações se servem.
 *
 *  É de propósito uma estrutura simples e não o modelo vivo: assim a
 *  resolução testa-se sem viewer, e quem a preenche fica com a
 *  responsabilidade de a ler do sítio certo. */
export interface DadosDocumento {
  /** Nome do ficheiro do modelo principal. */
  ficheiro: string | null
  /** IFC2X3, IFC4, ... */
  esquema: string | null
  projeto: string | null
  /** Quantos modelos estão carregados. */
  modelos: number
  /** Total de entidades dos modelos carregados. */
  entidades: number
  /** Quantos elementos por classe IFC, para a tabela. */
  porClasse: { classe: string; contagem: number }[]
  /** Pisos encontrados na estrutura espacial. */
  pisos: string[]
  /** Tópicos BCF, para o bloco respectivo. */
  topicos: { titulo: string; estado: string | null; responsavel: string | null }[]
  /** A data em que o documento é gerado. */
  data: Date
}

export function dadosVazios(): DadosDocumento {
  return {
    ficheiro: null,
    esquema: null,
    projeto: null,
    modelos: 0,
    entidades: 0,
    porClasse: [],
    pisos: [],
    topicos: [],
    data: new Date(),
  }
}

/** As ligações que se podem usar num texto, com o que cada uma dá.
 *  Esta lista alimenta a ajuda do editor: uma ligação que não esteja
 *  aqui não existe. */
export const LIGACOES: { chave: string; descricao: string }[] = [
  { chave: 'ficheiro', descricao: 'Nome do ficheiro do modelo' },
  { chave: 'esquema', descricao: 'Versão do esquema IFC' },
  { chave: 'projeto', descricao: 'Nome do projeto (IfcProject)' },
  { chave: 'modelos', descricao: 'Quantos modelos estão carregados' },
  { chave: 'entidades', descricao: 'Total de entidades' },
  { chave: 'classes', descricao: 'Quantas classes IFC distintas' },
  { chave: 'pisos', descricao: 'Quantos pisos' },
  { chave: 'lista-pisos', descricao: 'Os pisos, separados por vírgula' },
  { chave: 'topicos', descricao: 'Quantos tópicos BCF' },
  { chave: 'data', descricao: 'Data de hoje' },
]

const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
]

/** Data e números escritos à mão, e não com `toLocaleString('pt-PT')`.
 *
 *  O `toLocaleString` depende dos dados de idioma do ambiente. Num Node
 *  sem ICU completo, 1234 sai "1234" e não "1.234", e o mês sai em
 *  inglês. Um relatório que muda de formato conforme o browser de quem
 *  o gera não serve, e foi um teste a apanhar isto. */
export function formatarData(d: Date): string {
  const dia = String(d.getDate()).padStart(2, '0')
  return `${dia} de ${MESES[d.getMonth()]} de ${d.getFullYear()}`
}

/** Milhares separados por ponto, como se escreve em Portugal. */
export function formatarNumero(n: number): string {
  if (!Number.isFinite(n)) return '0'
  const sinal = n < 0 ? '-' : ''
  const inteiro = Math.abs(Math.trunc(n)).toString()
  return sinal + inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}

/** O valor de uma ligação, ou null se a ligação não existir. */
export function valorDaLigacao(
  chave: string,
  dados: DadosDocumento,
): string | null {
  switch (chave.trim()) {
    case 'ficheiro':
      return dados.ficheiro
    case 'esquema':
      return dados.esquema
    case 'projeto':
      return dados.projeto
    case 'modelos':
      return String(dados.modelos)
    case 'entidades':
      return formatarNumero(dados.entidades)
    case 'classes':
      return String(dados.porClasse.length)
    case 'pisos':
      return String(dados.pisos.length)
    case 'lista-pisos':
      return dados.pisos.length > 0 ? dados.pisos.join(', ') : null
    case 'topicos':
      return String(dados.topicos.length)
    case 'data':
      return formatarData(dados.data)
    default:
      return null
  }
}

/**
 * Substitui as ligações pelo que valem agora.
 *
 * Uma ligação que não resolve fica escrita como `[chave: motivo]` e não
 * como texto vazio. Um relatório com um buraco silencioso é pior do que
 * um que diz onde está o buraco: quem o lê tem de poder ver que ali
 * faltava um número.
 */
export function resolverTexto(
  texto: string,
  dados: DadosDocumento,
): string {
  return texto.replace(/\{([^{}]*)\}/g, (_todo, chave: string) => {
    const limpa = chave.trim()
    if (limpa === '') return '[ligação vazia]'
    const conhecida = LIGACOES.some((l) => l.chave === limpa)
    if (!conhecida) return `[${limpa}: não existe]`
    const valor = valorDaLigacao(limpa, dados)
    if (valor === null || valor === '') return `[${limpa}: sem valor]`
    return valor
  })
}

/** As ligações usadas num texto, para o editor poder avisar. */
export function ligacoesUsadas(texto: string): string[] {
  const achadas = [...texto.matchAll(/\{([^{}]*)\}/g)].map((m) => m[1].trim())
  return [...new Set(achadas)]
}

/** As ligações de um texto que não existem. */
export function ligacoesDesconhecidas(texto: string): string[] {
  const validas = new Set(LIGACOES.map((l) => l.chave))
  return ligacoesUsadas(texto).filter((c) => c !== '' && !validas.has(c))
}

export function documentoInicial(): Documento {
  return {
    id: `doc-${Date.now()}`,
    nome: 'Relatório do modelo',
    blocos: [
      { id: 'b1', tipo: 'titulo', texto: 'Relatório do modelo' },
      {
        id: 'b2',
        tipo: 'paragrafo',
        texto:
          'Modelo: {ficheiro} ({esquema}). Relatório gerado a {data}.',
      },
      { id: 'b3', tipo: 'subtitulo', texto: 'Conteúdo' },
      {
        id: 'b4',
        tipo: 'paragrafo',
        texto:
          'O modelo tem {entidades} entidades, de {classes} classes IFC ' +
          'distintas, distribuídas por {pisos} pisos: {lista-pisos}.',
      },
      { id: 'b5', tipo: 'tabela-classes', texto: '' },
    ],
  }
}

export const DOCUMENTOS_KEY = 'vn2r.documentos'

interface Armazenamento {
  getItem: (chave: string) => string | null
  setItem: (chave: string, valor: string) => void
}

function armazenamento(): Armazenamento | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

const TIPOS: TipoBloco[] = [
  'titulo',
  'subtitulo',
  'paragrafo',
  'tabela-classes',
  'topicos-bcf',
  'quebra',
]

export function documentoValido(valor: unknown): valor is Documento {
  if (typeof valor !== 'object' || valor === null) return false
  const d = valor as Partial<Documento>
  if (typeof d.id !== 'string' || typeof d.nome !== 'string') return false
  if (!Array.isArray(d.blocos)) return false
  return d.blocos.every((b) => {
    if (typeof b !== 'object' || b === null) return false
    const x = b as Partial<BlocoDocumento>
    return (
      typeof x.id === 'string' &&
      typeof x.texto === 'string' &&
      typeof x.tipo === 'string' &&
      TIPOS.includes(x.tipo as TipoBloco)
    )
  })
}

export function lerDocumentos(
  store: Armazenamento | null = armazenamento(),
): Documento[] {
  try {
    const bruto = store?.getItem(DOCUMENTOS_KEY)
    if (!bruto) return []
    const lido: unknown = JSON.parse(bruto)
    if (!Array.isArray(lido)) return []
    return lido.filter(documentoValido)
  } catch {
    return []
  }
}

export function gravarDocumentos(
  documentos: readonly Documento[],
  store: Armazenamento | null = armazenamento(),
): void {
  try {
    store?.setItem(DOCUMENTOS_KEY, JSON.stringify(documentos))
  } catch {
    /* sem onde guardar: o documento dura esta sessão */
  }
}
